import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import {
  caseExpensePath,
  caseTimeEntryPath,
  employeesActivityPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

/* -- Types ------------------------------------------------ */

interface ActivityEntry {
  id: string
  source_id: number
  case_id: number | null
  activity_type: "Time" | "Expense"
  activity_date: string
  employee_name: string | null
  case_name: string | null
  sub_type: string | null
  description: string
  added_at: string
  submitted_at: string | null
  approved_at: string | null
  paid_at: string | null
}

interface Filters {
  start_date: string
  end_date: string
  employee_id: string | null
  type: string | null
}

interface FilterOptions {
  employees?: { id: number; name: string }[]
}

interface Totals {
  otj_display?: string | null
  travel_display?: string | null
  mileage_display?: string | null
  expense_display?: string | null
}

interface Props {
  activities: ActivityEntry[]
  viewing_self: boolean
  totals: Totals | null
  filters: Filters
  filter_options: FilterOptions
}

/* -- Helpers ---------------------------------------------- */

function formatDate(dateStr: string | null): string {
  return formatDateUtil(dateStr, {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  })
}

function formatShortDate(dateStr: string | null): string {
  return formatDateUtil(dateStr, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  })
}

function entryHref(entry: ActivityEntry): string | null {
  if (!entry.case_id) return null
  if (entry.activity_type === "Time") {
    return caseTimeEntryPath(entry.case_id, entry.source_id)
  }
  return caseExpensePath(entry.case_id, entry.source_id)
}

/* -- Constants -------------------------------------------- */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Activity", href: employeesActivityPath() },
  { title: "History", href: employeesActivityPath() },
]

/* -- Component -------------------------------------------- */

export default function ActivityIndex({
  activities,
  viewing_self,
  totals,
  filters,
  filter_options,
}: Props) {
  const showEmployeeColumn = !viewing_self
  const [startDate, setStartDate] = useState(filters.start_date)
  const [endDate, setEndDate] = useState(filters.end_date)
  const [employeeId, setEmployeeId] = useState(filters.employee_id ?? "")
  const [activityType, setActivityType] = useState(filters.type ?? "")

  function handleFilter(e: React.FormEvent) {
    e.preventDefault()
    const params: Record<string, string> = {
      start_date: startDate,
      end_date: endDate,
    }
    if (employeeId) params.employee_id = employeeId
    if (activityType) params.type = activityType
    router.get(employeesActivityPath(), params, { preserveState: true })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Activity History" />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">
          {viewing_self ? "My Activity History" : "Activity History"}
        </h1>

        {/* Filter form */}
        <form
          onSubmit={handleFilter}
          className="mb-4 flex flex-wrap items-end gap-4"
        >
          <div>
            <label
              htmlFor="start_date"
              className="text-hpi-med-gray block text-xs font-medium uppercase"
            >
              Start Date
            </label>
            <input
              type="date"
              id="start_date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border-hpi-light-gray mt-1 rounded border px-3 py-1.5 text-sm"
            />
          </div>
          <div>
            <label
              htmlFor="end_date"
              className="text-hpi-med-gray block text-xs font-medium uppercase"
            >
              End Date
            </label>
            <input
              type="date"
              id="end_date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border-hpi-light-gray mt-1 rounded border px-3 py-1.5 text-sm"
            />
          </div>
          {!viewing_self && filter_options.employees && (
            <div>
              <label
                htmlFor="employee_id"
                className="text-hpi-med-gray block text-xs font-medium uppercase"
              >
                Employee
              </label>
              <select
                id="employee_id"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                className="border-hpi-light-gray mt-1 rounded border px-3 py-1.5 text-sm"
              >
                <option value="">All Employees</option>
                {filter_options.employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label
              htmlFor="type"
              className="text-hpi-med-gray block text-xs font-medium uppercase"
            >
              Type
            </label>
            <select
              id="type"
              value={activityType}
              onChange={(e) => setActivityType(e.target.value)}
              className="border-hpi-light-gray mt-1 rounded border px-3 py-1.5 text-sm"
            >
              <option value="">All</option>
              <option value="T">Time</option>
              <option value="E">Expenses</option>
              <option value="M">Mileage</option>
            </select>
          </div>
          <button
            type="submit"
            className="bg-hpi-blue rounded px-4 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            Filter
          </button>
        </form>

        {activities.length === 0 ? (
          <div className="rounded border border-gray-200 bg-gray-50 px-6 py-8 text-center">
            <p className="text-lg font-medium text-gray-600">
              No activity found
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting the date range or filters.
            </p>
          </div>
        ) : (
          <>
            <div className="border-hpi-light-gray overflow-x-auto rounded border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-hpi-light-gray text-hpi-med-gray border-b bg-gray-50 text-left text-xs font-medium tracking-wider uppercase">
                    <th className="px-4 py-2">Date</th>
                    {showEmployeeColumn && <th className="px-4 py-2">Name</th>}
                    <th className="px-4 py-2">Case</th>
                    <th className="px-4 py-2">Type</th>
                    <th className="px-4 py-2">Description</th>
                    <th className="px-4 py-2">Added</th>
                    <th className="px-4 py-2">Submitted</th>
                    <th className="px-4 py-2">Approved</th>
                    <th className="px-4 py-2">Paid</th>
                  </tr>
                </thead>
                <tbody className="divide-hpi-light-gray divide-y bg-white">
                  {activities.map((entry) => (
                    <ActivityRow
                      key={entry.id}
                      entry={entry}
                      showEmployeeColumn={showEmployeeColumn}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {totals && (
              <div className="mt-4 flex justify-center gap-6 text-sm font-bold">
                {totals.otj_display && <span>OTJ: {totals.otj_display}</span>}
                {totals.travel_display && (
                  <span>Travel: {totals.travel_display}</span>
                )}
                {totals.mileage_display && (
                  <span>Mileage: {totals.mileage_display}</span>
                )}
                {totals.expense_display && (
                  <span>Total: {totals.expense_display}</span>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  )
}

/* -- Row -------------------------------------------------- */

function ActivityRow({
  entry,
  showEmployeeColumn,
}: {
  entry: ActivityEntry
  showEmployeeColumn: boolean
}) {
  const href = entryHref(entry)
  const typeLabel = entry.sub_type
    ? `${entry.activity_type}/${entry.sub_type}`
    : entry.activity_type

  return (
    <tr className="hover:bg-hpi-pale-blue">
      <td className="px-4 py-2 whitespace-nowrap">
        <CellLink href={href}>{formatDate(entry.activity_date)}</CellLink>
      </td>
      {showEmployeeColumn && (
        <td className="px-4 py-2 whitespace-nowrap">
          <CellLink href={href}>{entry.employee_name ?? "-"}</CellLink>
        </td>
      )}
      <td className="px-4 py-2">
        <CellLink href={href}>{entry.case_name ?? "-"}</CellLink>
      </td>
      <td className="px-4 py-2 whitespace-nowrap">
        <CellLink href={href}>{typeLabel}</CellLink>
      </td>
      <td className="px-4 py-2">
        <CellLink href={href}>{entry.description}</CellLink>
      </td>
      <td className="px-4 py-2 whitespace-nowrap">
        {formatShortDate(entry.added_at)}
      </td>
      <td className="px-4 py-2 whitespace-nowrap">
        {formatShortDate(entry.submitted_at)}
      </td>
      <td className="px-4 py-2 whitespace-nowrap">
        {formatShortDate(entry.approved_at)}
      </td>
      <td className="px-4 py-2 whitespace-nowrap">{entry.paid_at ?? ""}</td>
    </tr>
  )
}

/* -- Cell Link -------------------------------------------- */

function CellLink({
  href,
  children,
}: {
  href: string | null
  children: React.ReactNode
}) {
  if (!href) {
    return <span className="text-hpi-dark-gray">{children}</span>
  }
  return (
    <Link href={href} className="text-hpi-blue hover:underline" target="_blank">
      {children}
    </Link>
  )
}
